import React from 'react';
import PropTypes from 'prop-types';
import Form from '../../Form';
import Loading from './../../Loading';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { prepareDecimalFields } from './../../../utils/format';
import { getTranslationFor } from 'src/locale/lang';


const isRangeValid = (value, start, end) => {
    const numberValue = parseFloat(value);

    return !isNaN(numberValue) && numberValue >= start && numberValue <= end;
};

class WidgetSettingsForm extends Form {
    static propTypes = {
        status: PropTypes.string
    };

    static defaultProps = {
        data: ImmutablePropTypes.map,

        getWidgetSettings: PropTypes.func.isRequired,
        putWidgetSettings: PropTypes.func.isRequired
    };

    static percentageFields = [
        'liablimit_threshold',
        'multiples_risk_above_1',
        'multiples_risk_above_2',
        'multiples_risk_above_3'
    ];

    static fieldsValidation = {
        'liablimit_threshold': [
            value => value && !isRangeValid(value, 0, 100) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'multiples_payout_above_1': [
            value => value && !isRangeValid(value, 0, 1000000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'multiples_risk_above_1': [
            value => value && !isRangeValid(value, 0, 100) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'multiples_payout_above_2': [
            value => value && !isRangeValid(value, 0, 1000000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'multiples_risk_above_2': [
            value => value && !isRangeValid(value, 0, 100) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'multiples_payout_above_3': [
            value => value && !isRangeValid(value, 0, 1000000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'multiples_risk_above_3': [
            value => value && !isRangeValid(value, 0, 100) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'lastlegrum_payout_above': [
            value => value && !isRangeValid(value, 0, 10000000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'avgnetpo_stakes_above': [
            value => value && !isRangeValid(value, 0, 1000000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'avgnetpo_payout_above': [
            value => value && !isRangeValid(value, 0, 10000000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ],
        'avgnetpo_net_payout_below': [
            value => value && !isRangeValid(value, -10000, 10000) ? getTranslationFor('ERROR_INCORECT_VALUE') : null
        ]
    };

    constructor(props, context) {
        super(props, context);
    }

    componentDidMount() {
        this.props.getWidgetSettings();
    }

    UNSAFE_componentWillReceiveProps(props) {
        if (props.data !== this.props.data && status !== 'IN_PROGRESS') {
            this.updateSettings(props);
        }
    }

    updateSettings(props) {
        const { data } = props;

        // Init form values
        this.setState({
            data: data.reduce((out, value, key) => {
                out[key] = WidgetSettingsForm.percentageFields.includes(key) ? value * 100 : value;

                return out;
            }, {})
        });
    }

    onSubmit(event) {
        event.preventDefault();

        const { data } = this.state;

        Form.prototype.onSubmit.call(this, event);

        if ( !this.isValid() ) {
            return false;
        }

        // Convert string number to number
        const updatedData = Object.keys(data).reduce((out, key) => {
            const value = isNaN(data[key]) ? data[key] : parseFloat(data[key]);

            // Convert percentage fields to decimal
            out[key] = WidgetSettingsForm.percentageFields.includes(key) ? value / 100 : data[key];
            return out;
        }, {});

        this.props.putWidgetSettings(updatedData);
    }

    renderForm() {
        const payoutsAndRisksAbove = [ 1, 2, 3 ];

        return (
            <div className='widget-settings-form'>

                <Loading active={ this.props.status === 'IN_PROGRESS' } message='Loading widget settings ...' />

                <div className='row'>
                    <div className='col-3'>
                        <h4 className='form-field-title'>Liability Limits</h4>
                    </div>

                    <div className='col-2'>
                        {
                            this.getField(
                                <input
                                    type='text'
                                    label='Liability limit warning %'
                                    { ...this.getFieldParameters('liablimit_threshold') }
                                />
                            )
                        }
                    </div>
                </div>

                <div className='row'>
                    <div className='col-3'>
                        <h4 className='form-field-title'>Multiples - risk vs payout</h4>
                    </div>

                    <div className='col-8'>
                        {
                            payoutsAndRisksAbove.map((item, index) => {
                                return (
                                    <div className='row' key={item}>
                                        <div className='col-3'>
                                            {
                                                this.getField(
                                                    <input
                                                        type='text'
                                                        label='Payout above'
                                                        {...this.getFieldParameters(`multiples_payout_above_${item}`)}
                                                    />
                                                )
                                            }
                                        </div>

                                        <div className='col-1'><span className='form-field-words'>and</span> </div>

                                        <div className='col-3'>
                                            {
                                                this.getField(
                                                    <input
                                                        type='text'
                                                        label='Risk % above'
                                                        {...this.getFieldParameters(`multiples_risk_above_${item}`)}
                                                    />
                                                )
                                            }
                                        </div>
                                        {
                                            payoutsAndRisksAbove.length - 1 !== index ? (
                                                <div className='col-1'>
                                                    <span className='form-field-words'>or</span>
                                                </div>
                                            ) : void 0
                                        }
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                <div className='row'>
                    <div className='col-3'>
                        <h4 className='form-field-title'>Last leg RUM selections</h4>
                    </div>

                    <div className='col-2'>
                        {
                            this.getField(
                                <input
                                    type='text'
                                    label='Last leg payout above'
                                    { ...this.getFieldParameters('lastlegrum_payout_above') }
                                />
                            )
                        }
                    </div>
                </div>

                <div className='row'>
                    <div className='col-3'>
                        <h4 className='form-field-title'>Average net payout</h4>
                    </div>

                    <div className='col-8'>
                        <div className='row'>
                            <div className='col-3'>
                                {
                                    this.getField(
                                        <input
                                            type='text'
                                            label='Selection line stakes above'
                                            { ...this.getFieldParameters('avgnetpo_stakes_above') }
                                        />
                                    )
                                }
                            </div>

                            <div className='col-1'><span className='form-field-words'>and</span> </div>

                            <div className='col-3'>
                                {
                                    this.getField(
                                        <input
                                            type='text'
                                            label='Payout above'
                                            { ...this.getFieldParameters('avgnetpo_payout_above') }
                                        />
                                    )
                                }
                            </div>

                            <div className='col-1'><span className='form-field-words'>and</span> </div>

                            <div className='col-3'>
                                {
                                    this.getField(
                                        <input
                                            type='text'
                                            label='Average net payout below'
                                            { ...this.getFieldParameters('avgnetpo_net_payout_below') }
                                        />
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>

                <div className='buttons'>
                    <button className='btn btn-primary' type='submit'>
                        Save
                    </button>
                </div>
            </div>
        );
    }
}
export default WidgetSettingsForm;
